import { type OptionsMap } from 'src/types';

// Intentionally loose – avoids importing React into the model layer.
// The actual component is registered from the UI layer via registerComponent().
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FilterComponent = (props: any) => any;

export abstract class FilterConfig {
  readonly code: string;
  readonly label: string;
  readonly queryField: string;
  readonly optionsProvider: ((params: string[]) => Promise<OptionsMap>) | null;
  private _component: FilterComponent | null = null;

  constructor(
    code: string,
    label: string,
    queryField: string,
    optionsProvider: ((params: string[]) => Promise<OptionsMap>) | null = null,
  ) {
    this.code = code;
    this.label = label;
    this.queryField = queryField;
    this.optionsProvider = optionsProvider;
  }

  /**
   * Called once from the UI layer to bind the React component for this filter.
   * Keeps the model layer free of any React/UI imports.
   */
  registerComponent(component: FilterComponent): void {
    this._component = component;
  }

  get component(): FilterComponent | null {
    return this._component;
  }

  abstract get defaultValue(): unknown;

  /** Whether this filter should be excluded from the reset action and canReset() check. */
  get excludeFromReset(): boolean {
    return false;
  }

  /** Whether this filter value should be omitted from URL serialization. */
  get hideInUrl(): boolean {
    return false;
  }

  /** Human-readable summary of the current filter value, shown in applied-filters chips. */
  abstract toLabel(filterData: unknown): string | null;

  /** Whether the given value should be treated as "not set" for this filter. */
  abstract isEmpty(value: unknown): boolean;

  /** Convert the stored filter value to a shape accepted by the API query. */
  abstract toQuery(filterData: unknown): unknown;

  /** Convert the stored filter value to a URL-safe string. */
  abstract toUrl(filterData: unknown): string | null;

  /**
   * Parse a raw URL param string (and optional options map) back to the
   * internal filter value shape.
   */
  abstract fromUrl(paramValue: string, options: OptionsMap): unknown;

  /**
   * Resolve the options map needed to deserialize this filter from a URL param.
   * Base implementation calls optionsProvider without any arguments.
   */
  async resolveOptions(_paramValue: string): Promise<OptionsMap> {
    if (!this.optionsProvider) return {};
    return this.optionsProvider([]);
  }
}
